import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { Capacitor } from '@capacitor/core';
import { IconSetService } from '@coreui/icons-angular';
import { ToastrService } from 'ngx-toastr';
import PullToRefresh from 'pulltorefreshjs';
import { filter } from 'rxjs';
import { readToken } from './_auth/token-store';
import { iconSubset } from './icons/icon-subset';
import { FcmService } from './services/fcm.service';
import { RestserviceService } from './services/restservice.service';
import { ThemeService } from './services/theme.service';

@Component({
  selector: 'app-root',
  template: ` <router-outlet /> `,
  imports: [RouterOutlet, CommonModule],
})
export class AppComponent implements OnInit {
  title = 'Auton Systems';
  showBanner = false;
  fcmToken = '';

  constructor(
    private router: Router,
    private titleService: Title,
    private iconSetService: IconSetService,
    private fcmService: FcmService,
    private service: RestserviceService,
    private themeService: ThemeService,
    private swUpdate: SwUpdate,
    private toastr: ToastrService,
  ) {
    this.titleService.setTitle(this.title);
    this.iconSetService.icons = { ...iconSubset };
    // Restore saved / system theme before first paint of app content.
    this.themeService.init();
  }
  ngOnInit(): void {
    // 1. Initialize Firebase Cloud Messaging automatically on boot
    this.fcmService.initialize().then(() => {
      setTimeout(() => {
        this.fcmToken = this.fcmService.getToken();

        if (this.fcmToken) {
          localStorage.setItem('fcm_token', this.fcmToken);

          // Register with backend if already logged in
          if (readToken()) {
            this.service.registerToken(this.fcmToken);
          }
        }
      }, 2000);
    });

    // 2. Initialize Pull To Refresh globally for Android Devices
    if (Capacitor.isNativePlatform()) {
      PullToRefresh.init({
        mainElement: 'body',
        onRefresh() {
          window.location.reload();
        },
      });
    }

    this.router.events.subscribe((evt) => {
      if (!(evt instanceof NavigationEnd)) {
        return;
      }
    });

    // 3. When the Angular service worker has a new version cached, prompt the
    // user to reload rather than silently serving stale code or auto-reloading
    // mid-task. (No-op in dev, where the SW is disabled.)
    if (this.swUpdate.isEnabled) {
      this.swUpdate.versionUpdates
        .pipe(
          filter(
            (e): e is VersionReadyEvent => e.type === 'VERSION_READY',
          ),
        )
        .subscribe(() => {
          const toast = this.toastr.info(
            'A new version is available. Click here to update.',
            'Update ready',
            { disableTimeOut: true, tapToDismiss: false, closeButton: true },
          );
          toast?.onTap?.subscribe(() =>
            this.swUpdate
              .activateUpdate()
              .then(() => document.location.reload()),
          );
        });
    }
  }

  // Helper method to copy token in dev environment
  copyToken() {
    if (this.fcmToken && navigator.clipboard) {
      navigator.clipboard.writeText(this.fcmToken).then(() => {
        alert('FCM Token Copied!');
      });
    }
  }
}
